import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, X, Sparkles, Search } from "lucide-react";
import { toast } from "sonner";

export default function OpcionesRapidas() {
  const [opts, setOpts] = useState([]);
  const [productos, setProductos] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ texto: "", producto_id: "ALL" });

  const load = async () => {
    const [o, p] = await Promise.all([
      api.get("/quick-options"),
      api.get("/productos"),
    ]);
    setOpts(o.data);
    setProductos(p.data);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return opts;
    const t = q.toLowerCase();
    return opts.filter((o) =>
      o.texto.toLowerCase().includes(t) ||
      productos.find((p) => p.id === o.producto_id)?.nombre.toLowerCase().includes(t)
    );
  }, [opts, productos, q]);

  const save = async () => {
    if (!form.texto.trim()) return toast.error("Texto requerido");
    try {
      await api.post("/quick-options", {
        texto: form.texto.trim(),
        producto_id: form.producto_id === "ALL" ? null : form.producto_id,
      });
      toast.success("Opción guardada");
      setOpen(false);
      setForm({ texto: "", producto_id: "ALL" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/quick-options/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  // Agrupar por producto
  const grouped = useMemo(() => {
    const g = { _global: [] };
    filtered.forEach((o) => {
      if (!o.producto_id) g._global.push(o);
      else {
        if (!g[o.producto_id]) g[o.producto_id] = [];
        g[o.producto_id].push(o);
      }
    });
    return g;
  }, [filtered]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-3xl font-black tracking-tight flex items-center gap-2">
            <Sparkles className="h-7 w-7 text-primary" />Opciones rápidas
          </h2>
          <p className="text-sm text-muted-foreground mt-1">Sugerencias predefinidas (Sin cebolla, Con todo, etc.) por producto o globales.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-opcion-btn"><Plus className="h-4 w-4 mr-1" />Nueva opción</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nueva opción rápida</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Texto *</Label><Input value={form.texto} onChange={(e) => setForm({ ...form, texto: e.target.value })} placeholder="Ej. Sin cilantro" data-testid="opcion-texto-input" /></div>
              <div>
                <Label>Aplica a</Label>
                <Select value={form.producto_id} onValueChange={(v) => setForm({ ...form, producto_id: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">Global (todos los productos)</SelectItem>
                    {productos.map((p) => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button onClick={save} data-testid="save-opcion-btn">Guardar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Buscar por opción o producto…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-11" />
      </div>

      {grouped._global.length > 0 && (
        <Card>
          <CardContent className="p-5">
            <h3 className="text-sm uppercase tracking-widest text-muted-foreground mb-3">Globales</h3>
            <div className="flex flex-wrap gap-2">
              {grouped._global.map((o) => (
                <span key={o.id} className="inline-flex items-center gap-1 px-3 py-1.5 bg-secondary rounded-full text-sm font-semibold" data-testid={`opcion-global-${o.id}`}>
                  ⚡ {o.texto}
                  <button onClick={() => remove(o.id)} className="text-muted-foreground hover:text-destructive ml-1"><X className="h-3 w-3" /></button>
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        {productos.filter((p) => grouped[p.id]?.length > 0).map((p) => (
          <Card key={p.id}>
            <CardContent className="p-5">
              <div className="font-bold text-lg mb-3">{p.nombre}</div>
              <div className="flex flex-wrap gap-2">
                {grouped[p.id].map((o) => (
                  <span key={o.id} className="inline-flex items-center gap-1 px-3 py-1.5 bg-amber-100 dark:bg-amber-900/40 text-amber-900 dark:text-amber-100 rounded-full text-sm font-semibold" data-testid={`opcion-${o.id}`}>
                    {o.texto}
                    <button onClick={() => remove(o.id)} className="hover:text-destructive ml-1"><X className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filtered.length === 0 && (
        <Card><CardContent className="p-12 text-center text-muted-foreground">No hay opciones rápidas configuradas.</CardContent></Card>
      )}
    </div>
  );
}
